import { useMemo } from 'react';
import {
  Subscription,
  useShopifySubscriptionDetails,
} from '@/features/shop-settings/useShopifySubscriptionDetails';
import { maybe } from '@/features/details/utils';
import { AccountDetails } from '@/features/account-context';

/**
 * Hook to get merged subscription details from Shopify and account context.
 * Overrides Shopify's trialEnds with account's trialEnds if it's later.
 * @param account - The current account details
 * @returns An object containing the merged subscription
 */
export function useMergedSubscription(account: AccountDetails) {
  const { subscription: _subscription } = useShopifySubscriptionDetails(
    maybe(() => account?.store?.alias),
  );

  const subscription = useMemo(() => {
    const accountTrialEnds = account?.store?.subscription?.trialEnds;
    if (!accountTrialEnds) {
      return _subscription;
    }

    const currentTrialEnds = _subscription?.trialEnds;
    if (!currentTrialEnds) {
      return { ..._subscription, trialEnds: accountTrialEnds } as Subscription;
    }

    const maxTrialEnds =
      new Date(currentTrialEnds) > new Date(accountTrialEnds)
        ? currentTrialEnds
        : accountTrialEnds;

    return { ..._subscription, trialEnds: maxTrialEnds } as Subscription;
  }, [_subscription, account?.store?.subscription?.trialEnds]);

  return { subscription };
}
